import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Offcanvas from "react-bootstrap/Offcanvas";
import moment from "moment";
import { toast } from "react-toastify";
import LoaderSpinner from "../../common/Loader";
import PnLStatusChip from "./PnLStatusChip";
import PnLCloseReturns, { canCloseReturns } from "./PnLCloseReturns";
import { getPnlRowDetailService } from "../../../services/pnlReport.service";
import {
  DETAIL_LINE_LIMIT,
  formatMoney,
  getAdjustmentLines,
  getCategoryLabel,
  getLockText,
  getStatusInfo,
  loadEntityList,
  monthLabel,
} from "./pnlReportUtils";

const formatPostedDate = (value) => {
  const parsed = moment.utc(value);
  return value && parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
};

const buildDetailRequest = (row) => ({
  year: row.year,
  month: row.month,
  category: row.category,
  subType: row.subType,
  companySellerId: row.companySellerId ?? null,
});

const ChangeLog = ({ lines }) => {
  const adjustments = getAdjustmentLines(lines);
  return (
    <section className="pnl-detail-section">
      <h3>Change log</h3>
      {adjustments.length === 0 && <p className="pnl-detail-empty">No adjustments posted for this row.</p>}
      {adjustments.map((line) => (
        <div className="pnl-log-item" key={`${line.sourceTable}-${line.sourceId}`}>
          <span className={`pnl-log-amount ${line.adjustment < 0 ? "pnl-neg" : "pnl-pos"}`}>
            {line.adjustment > 0 ? "+" : ""}{formatMoney(line.adjustment)}
          </span>
          {line.reference}
          {line.description && <small>{line.description}</small>}
          <small>Posted on {formatPostedDate(line.postedDate)}</small>
        </div>
      ))}
    </section>
  );
};

ChangeLog.propTypes = { lines: PropTypes.array.isRequired };

const TransactionLines = ({ lines }) => {
  const visible = lines.slice(0, DETAIL_LINE_LIMIT);
  return (
    <section className="pnl-detail-section">
      <h3>Transactions ({lines.length})</h3>
      {lines.length === 0 && <p className="pnl-detail-empty">No transactions found for this row.</p>}
      {lines.length > 0 && (
        <table className="pnl-lines">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Date</th>
              <th className="pnl-num">Gross</th>
              <th className="pnl-num">Net</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((line) => (
              <tr key={`${line.sourceTable}-${line.sourceId}`}>
                <td>{line.reference}</td>
                <td>{formatPostedDate(line.postedDate)}</td>
                <td className="pnl-num">{formatMoney(line.gross)}</td>
                <td className="pnl-num">{formatMoney(line.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lines.length > DETAIL_LINE_LIMIT && (
        <p className="pnl-detail-note">Showing the first {DETAIL_LINE_LIMIT} of {lines.length} transactions.</p>
      )}
    </section>
  );
};

TransactionLines.propTypes = { lines: PropTypes.array.isRequired };

const useRowDetail = (row) => {
  const [detail, setDetail] = useState({ loading: true, lines: [] });

  useEffect(() => {
    let ignore = false;
    setDetail({ loading: true, lines: [] });
    loadEntityList(() => getPnlRowDetailService(buildDetailRequest(row))).then(({ rows, error }) => {
      if (ignore) return;
      if (error) toast.error(error);
      setDetail({ loading: false, lines: rows });
    });
    return () => {
      ignore = true;
    };
  }, [row]);

  return detail;
};

const PnLDetailDrawer = ({ row, onClose, onReturnsClosed }) => {
  const detail = useRowDetail(row);

  const categoryLabel = getCategoryLabel(row.category);
  const title = categoryLabel === row.subType ? row.subType : `${categoryLabel}: ${row.subType}`;

  return (
    <Offcanvas show placement="end" onHide={onClose} className="pnl-drawer">
      <Offcanvas.Header closeButton>
        <div>
          <Offcanvas.Title as="h2" className="pnl-drawer-title">{title}</Offcanvas.Title>
          <div className="pnl-drawer-sub">{monthLabel(row.year, row.month)}</div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <PnLStatusChip status={getStatusInfo(row)} />
        <p className="pnl-lock-text">{getLockText(row)}</p>
        <div className="pnl-calc">
          <span>Gross amount</span><span>{formatMoney(row.grossAmount)}</span>
          <span>Cost</span><span>{formatMoney(row.costAmount)}</span>
          <span>Adjustments</span><span>{formatMoney(row.adjustmentAmount)}</span>
          <span className="pnl-calc-total">Net amount</span>
          <span className="pnl-calc-total">{formatMoney(row.netAmount)}</span>
        </div>
        {canCloseReturns(row) && <PnLCloseReturns row={row} onClosed={onReturnsClosed} />}
        {detail.loading && <LoaderSpinner />}
        {!detail.loading && (
          <>
            <ChangeLog lines={detail.lines} />
            <TransactionLines lines={detail.lines} />
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

PnLDetailDrawer.propTypes = {
  row: PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    subType: PropTypes.string,
    statusCode: PropTypes.number,
    grossAmount: PropTypes.number,
    costAmount: PropTypes.number,
    adjustmentAmount: PropTypes.number,
    netAmount: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onReturnsClosed: PropTypes.func.isRequired,
};

export default PnLDetailDrawer;
